import { ShoppingCart } from '@/entities/ShoppingCart';
import { ShippingMethod } from '@/entities/ShippingMethod';
import { OrderMethod } from '@/entities/OrderMethod';
import URI from 'urijs';
import { apiClient } from './api-client';
import { AxiosResponse } from 'axios';

/**
 * Service for shopping cart operations.
 */
export class ShoppingCartService {
    private get baseUrl(): URI {
        return new URI('/api/shopping-carts');
    }

    public async getShoppingCarts(): Promise<ShoppingCart[]> {
        const res = await apiClient.get<ShoppingCart[]>(this.baseUrl.toString());
        return res.data;
    }

    public async getOrderMethods(): Promise<OrderMethod[]> {
        const res = await apiClient.get<OrderMethod[]>(this.baseUrl.segmentCoded('order-methods').toString());
        return res.data;
    }

    public async setActiveShoppingCart(shoppingCartId: number): Promise<void> {
        await apiClient.put(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('active')
                .toString()
        );
    }

    public async addShoppingCart(cartName: string): Promise<number> {
        const res = await apiClient.put<number>(
            this.baseUrl
                .segmentCoded('new')
                .segmentCoded(cartName)
                .toString()
        );

        return res.data;
    }

    public async deleteShoppingCart(shoppingCartId: number): Promise<void> {
        await apiClient.delete(this.baseUrl.segmentCoded(shoppingCartId.toString(10)).toString());
    }

    public async addShoppingCartItem(shoppingCartId: number, fullPartId: string, quantity: number): Promise<void> {
        await apiClient.put(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .toString(),
            { fullPartId, quantity }
        );
    }

    public async addShoppingCartItemsBulk(
        shoppingCartId: number,
        items: Array<{ fullPartId: string; quantity: number }>
    ): Promise<void> {
        await apiClient.put(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .segmentCoded('bulk')
                .toString(),
            { items }
        );
    }

    public async updateShoppingCartMetadata(
        shoppingCartId: number,
        cartName: string,
        orderName: string
    ): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('metadata')
                .toString(),
            { cartName, orderName }
        );
    }

    public async updateShoppingCartShippingMethod(
        shoppingCartId: number,
        shippingMethod: ShippingMethod
    ): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('shipping')
                .segmentCoded(shippingMethod)
                .toString()
        );
    }

    public async updateShoppingCartOrderMethod(shoppingCartId: number, orderMethod: string): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('order-method')
                .segmentCoded(orderMethod)
                .toString()
        );
    }

    public async updateQuantityOfShoppingCartItem(
        shoppingCartId: number,
        shoppingCartItemId: number,
        quantity: number
    ): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .segmentCoded(shoppingCartItemId.toString(10))
                .segmentCoded('quantity')
                .segmentCoded(quantity.toString(10))
                .toString()
        );
    }

    public async updateUserInfoTextOfShoppingCartItem(
        shoppingCartId: number,
        shoppingCartItemId: number,
        userInfoText: string
    ): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .segmentCoded(shoppingCartItemId.toString(10))
                .segmentCoded('user-info-text')
                .toString(),
            { userInfoText }
        );
    }

    public async deleteShoppingCartItem(shoppingCartId: number, shoppingCartItemId: number): Promise<void> {
        await apiClient.delete(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .segmentCoded(shoppingCartItemId.toString(10))
                .toString()
        );
    }

    public async moveItemToShoppingCart(
        shoppingCartId: number,
        shoppingCartItemId: number,
        targetShoppingCartId: number
    ): Promise<void> {
        await apiClient.post(
            this.baseUrl
                .segmentCoded(shoppingCartId.toString(10))
                .segmentCoded('item')
                .segmentCoded(shoppingCartItemId.toString(10))
                .segmentCoded('move-to-cart')
                .segmentCoded(targetShoppingCartId.toString(10))
                .toString()
        );
    }
}
